/**
 * Development Debug Page
 * Live monitoring and debugging information - Only available in development mode
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { ENV, validateEnvironment } from '@/config/environment';
import { API_ENDPOINTS } from '@/shared/constants';
import { apiService, ApiCallLog } from '@/shared/services/apiService';
import { userService } from '@/features/user/service';
import { useSafeThemeClasses } from '@/shared/theme';

interface SystemHealth {
  environment: 'healthy' | 'warning' | 'error';
  api: 'connected' | 'disconnected' | 'checking';
  auth: 'authenticated' | 'unauthenticated' | 'expired';
  storage: 'available' | 'unavailable';
  features: Record<string, boolean>;
}

export default function DevDebugPage() {
  const theme = useSafeThemeClasses();
  const [apiCalls, setApiCalls] = useState<ApiCallLog[]>([]);
  const [systemHealth, setSystemHealth] = useState<SystemHealth>({
    environment: 'healthy',
    api: 'checking',
    auth: 'unauthenticated',
    storage: 'available',
    features: {}
  });
  const [showDetails, setShowDetails] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load API calls from service
  const loadApiCalls = useCallback(() => {
    setApiCalls(apiService.getApiCallLogs());
  }, []);

  // Health check functions
  const checkSystemHealth = useCallback(async () => {
    const health: SystemHealth = {
      environment: 'healthy',
      api: 'checking',
      auth: 'unauthenticated',
      storage: 'available',
      features: {
        analytics: ENV.features.analytics,
        blog: ENV.features.blog,
        projects: ENV.features.projects,
        errorReporting: ENV.features.errorReporting,
        performanceMonitoring: ENV.features.performanceMonitoring,
      }
    };

    // Check environment
    try {
      validateEnvironment();
      health.environment = 'healthy';
    } catch {
      health.environment = 'error';
    }

    // Check API connection
    try {
      await apiService.get('/uploads/health');
      health.api = 'connected';
    } catch {
      health.api = 'disconnected';
    }

    // Check authentication
    if (userService.isAuthenticated()) {
      health.auth = 'authenticated';
    } else {
      health.auth = 'unauthenticated';
    }

    // Check storage
    try {
      localStorage.setItem('test', 'test');
      localStorage.removeItem('test');
      health.storage = 'available';
    } catch {
      health.storage = 'unavailable';
    }

    setSystemHealth(health);
    loadApiCalls(); // Refresh API calls when checking health
  }, [loadApiCalls]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        checkSystemHealth();
        loadApiCalls();
      }, 5000);
      checkSystemHealth(); // Initial check
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, checkSystemHealth, loadApiCalls]);

  // Hide in production
  if (ENV.app.isProduction) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${theme.background}`}>
        <div className="text-center">
          <h1 className={`text-2xl font-bold ${theme.textError}`}>Access Denied</h1>
          <p className={theme.textSecondary}>Debug page is only available in development mode</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'connected':
      case 'authenticated':
      case 'available':
        return `${theme.textSuccess} ${theme.alertSuccess}`;
      case 'warning':
      case 'checking':
        return `${theme.textInfo} ${theme.alertInfo}`;
      case 'error':
      case 'disconnected':
      case 'unauthenticated':
      case 'expired':
      case 'unavailable':
        return `${theme.textError} ${theme.alertError}`;
      default:
        return `${theme.textMuted} ${theme.card}`;
    }
  };

  const formatValue = (value: unknown): string => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getEnvironmentInfo = () => {
    return {
      mode: ENV.app.environment,
      isDevelopment: ENV.app.isDevelopment,
      isProduction: ENV.app.isProduction,
      name: ENV.app.name,
      version: ENV.app.version,
      description: ENV.app.description,
      baseUrl: ENV.api.baseUrl,
      timeout: ENV.api.timeout,
      retries: ENV.api.retries,
      projectId: ENV.project.id,
      projectName: ENV.project.name,
      source: 'Environment variables loaded from .env file and import.meta.env',
      timestamp: new Date().toISOString()
    };
  };

  return (
    <div className={`min-h-screen p-6 ${theme.background}`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className={`${theme.card} rounded-lg p-6 mb-6`}>
          <div className="flex justify-between items-center">
            <div>
              <h1 className={`text-3xl font-bold ${theme.textHeading}`}>🔧 Development Debug Console</h1>
              <p className={`${theme.textSecondary} mt-2`}>Live system monitoring and debugging</p>
              <p className={`text-sm ${theme.textMuted} mt-1`}>
                Last updated: {new Date().toLocaleString()}
              </p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-4 py-2 rounded-lg font-medium ${
                  autoRefresh 
                    ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}` 
                    : `${theme.buttonSecondary} ${theme.buttonTextSecondary}`
                }`}
              >
                {autoRefresh ? '🔄 Auto Refresh ON' : '⏸️ Auto Refresh OFF'}
              </button>
              <button
                onClick={() => {
                  checkSystemHealth();
                  loadApiCalls();
                }}
                className={`px-4 py-2 rounded-lg ${theme.buttonPrimary} ${theme.buttonTextPrimary}`}
              >
                🔄 Refresh Now
              </button>
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className={`${theme.card} rounded-lg p-6`}>
            <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>🏥 System Health</h3>
            <div className="space-y-2">
              {Object.entries(systemHealth).map(([key, value]) => {
                if (key === 'features') return null;
                return (
                  <div key={key} className="flex justify-between items-center">
                    <span className={`text-sm ${theme.textSecondary} capitalize`}>{key}:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(value as string)}`}>
                      {value as string}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={`${theme.card} rounded-lg p-6`}>
            <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>🌍 Environment</h3>
            <div className={`space-y-2 text-sm ${theme.textSecondary}`}>
              <div><strong>Mode:</strong> {ENV.app.environment}</div>
              <div><strong>Name:</strong> {ENV.app.name}</div>
              <div><strong>Version:</strong> {ENV.app.version}</div>
              <div><strong>Project ID:</strong> {ENV.project.id.substring(0, 8)}...</div>
              <div><strong>Deployment:</strong> {window.location.origin}</div>
            </div>
          </div>

          <div className={`${theme.card} rounded-lg p-6`}>
            <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>🔗 API Configuration</h3>
            <div className={`space-y-2 text-sm ${theme.textSecondary}`}>
              <div><strong>Base URL:</strong> {ENV.api.baseUrl}</div>
              <div><strong>Timeout:</strong> {ENV.api.timeout}ms</div>
              <div><strong>Retries:</strong> {ENV.api.retries}</div>
              <div><strong>Project:</strong> {ENV.project.name}</div>
            </div>
          </div>

          <div className={`${theme.card} rounded-lg p-6`}>
            <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>🚀 Features</h3>
            <div className={`space-y-2 text-sm ${theme.textSecondary}`}>
              {Object.entries(systemHealth.features).map(([key, enabled]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="capitalize">{key}:</span>
                  <span className={`px-2 py-1 rounded text-xs ${enabled ? `${theme.alertSuccess} ${theme.textSuccess}` : `${theme.alertError} ${theme.textError}`}`}>
                    {enabled ? 'ON' : 'OFF'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Environment Variables & API Endpoints */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className={`${theme.card} rounded-lg p-6`}>
            <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>📝 Environment Configuration</h3>
            <div className={`${theme.input} rounded p-4 text-sm font-mono max-h-96 overflow-y-auto ${theme.textPrimary}`}>
              <pre>{JSON.stringify(getEnvironmentInfo(), null, 2)}</pre>
            </div>
          </div>

          <div className={`${theme.card} rounded-lg p-6`}>
            <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>🎯 API Endpoints</h3>
            <div className={`${theme.input} rounded p-4 text-sm font-mono max-h-96 overflow-y-auto ${theme.textPrimary}`}>
              <pre>{JSON.stringify(API_ENDPOINTS, null, 2)}</pre>
            </div>
          </div>
        </div>

        {/* Current User & Session */}
        <div className={`${theme.card} rounded-lg p-6 mb-6`}>
          <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>👤 Current Session</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className={`font-medium mb-2 ${theme.textSecondary}`}>User Data:</h4>
              <div className={`${theme.input} rounded p-4 text-sm font-mono ${theme.textPrimary}`}>
                <pre>{JSON.stringify(userService.getUser(), null, 2)}</pre>
              </div>
            </div>
            <div>
              <h4 className={`font-medium mb-2 ${theme.textSecondary}`}>Authentication:</h4>
              <div className={`space-y-2 text-sm ${theme.textPrimary}`}>
                <div><strong>Is Authenticated:</strong> {userService.isAuthenticated() ? 'Yes' : 'No'}</div>
                <div><strong>Token Available:</strong> {userService.getToken() ? 'Yes' : 'No'}</div>
                <div><strong>Token Preview:</strong> {userService.getToken()?.substring(0, 20)}...</div>
              </div>
            </div>
          </div>
        </div>

        {/* API Call History */}
        <div className={`${theme.card} rounded-lg p-6 mb-6`}>
          <div className="flex justify-between items-center mb-4">
            <h3 className={`font-semibold ${theme.textHeading}`}>📡 API Call History ({apiCalls.length} calls)</h3>
            <button
              onClick={() => {
                apiService.clearApiCallLogs();
                setApiCalls([]);
              }}
              className={`px-4 py-2 rounded-lg text-sm ${theme.buttonSecondary} ${theme.textError}`}
            >
              Clear History
            </button>
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {apiCalls.length === 0 ? (
              <div className={`text-center py-8 ${theme.textMuted}`}>
                No API calls recorded yet. Make some API calls to see them here.
                <br />
                <small className="text-xs">API calls are automatically logged when using the apiService.</small>
              </div>
            ) : (
              apiCalls.map((call) => (
                <div
                  key={call.id}
                  className={`${theme.card} rounded-lg p-4 cursor-pointer`}
                  onClick={() => setShowDetails(showDetails === call.id ? null : call.id)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        call.status === 'pending' ? `${theme.alertInfo} ${theme.textInfo}` :
                        typeof call.status === 'number' && call.status < 400 ? `${theme.alertSuccess} ${theme.textSuccess}` :
                        `${theme.alertError} ${theme.textError}`
                      }`}>
                        {call.method}
                      </span>
                      <span className={`font-mono text-sm truncate ${theme.textPrimary}`}>{call.url}</span>
                    </div>
                    <div className={`flex items-center space-x-3 text-sm ${theme.textMuted}`}>
                      <span>{call.status}</span>
                      {call.duration && <span>{call.duration}ms</span>}
                      <span>{new Date(call.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>

                  {showDetails === call.id && (
                    <div className="mt-4 pt-4">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-sm">
                        <div>
                          <h5 className={`font-medium mb-2 ${theme.textSecondary}`}>Headers:</h5>
                          <pre className={`${theme.input} p-2 rounded text-xs overflow-x-auto ${theme.textPrimary}`}>
                            {JSON.stringify(call.headers, null, 2)}
                          </pre>
                        </div>
                        {call.request ? (
                          <div>
                            <h5 className={`font-medium mb-2 ${theme.textSecondary}`}>Request:</h5>
                            <pre className={`${theme.input} p-2 rounded text-xs overflow-x-auto ${theme.textPrimary}`}>
                              <code>{formatValue(call.request)}</code>
                            </pre>
                          </div>
                        ) : null}
                        {call.response ? (
                          <div>
                            <h5 className={`font-medium mb-2 ${theme.textSecondary}`}>Response:</h5>
                            <pre className={`${theme.input} p-2 rounded text-xs overflow-x-auto ${theme.textPrimary}`}>
                              <code>{formatValue(call.response)}</code>
                            </pre>
                          </div>
                        ) : null}
                        {call.error && (
                          <div className="lg:col-span-3">
                            <h5 className={`font-medium mb-2 ${theme.textError}`}>Error:</h5>
                            <pre className={`${theme.alertError} p-2 rounded text-xs overflow-x-auto ${theme.textError}`}>
                              {call.error}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Storage Information */}
        <div className={`${theme.card} rounded-lg p-6 mt-6`}>
          <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>💾 Local Storage</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className={`font-medium mb-2 ${theme.textSecondary}`}>Stored Data:</h4>
              <div className={`space-y-2 text-sm max-h-48 overflow-y-auto ${theme.textPrimary}`}>
                {Object.keys(localStorage).length === 0 ? (
                  <div className={theme.textMuted}>No data in localStorage</div>
                ) : (
                  Object.keys(localStorage).map(key => (
                    <div key={key} className="flex justify-between items-center pb-1">
                      <span className="font-mono text-xs">{key}:</span>
                      <span className={`text-xs truncate max-w-xs ${theme.textMuted}`}>
                        {localStorage.getItem(key)?.substring(0, 50)}...
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div>
              <h4 className={`font-medium mb-2 ${theme.textSecondary}`}>Storage Usage:</h4>
              <div className={`text-sm space-y-2 ${theme.textPrimary}`}>
                <div>Items: {Object.keys(localStorage).length}</div>
                <div>
                  Size: {new Blob(Object.values(localStorage)).size} bytes
                </div>
                <div>
                  Available: {navigator.storage ? 'Yes' : 'Unknown'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Browser Information */}
        <div className={`${theme.card} rounded-lg p-6 mt-6`}>
          <h3 className={`font-semibold mb-3 ${theme.textHeading}`}>🌐 Browser & Network Info</h3>
          <div className={`grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm ${theme.textPrimary}`}>
            <div>
              <h4 className={`font-medium mb-2 ${theme.textSecondary}`}>Browser:</h4>
              <div className="space-y-1">
                <div><strong>User Agent:</strong> {navigator.userAgent.substring(0, 50)}...</div>
                <div><strong>Platform:</strong> {navigator.platform}</div>
                <div><strong>Language:</strong> {navigator.language}</div>
                <div><strong>Online:</strong> {navigator.onLine ? 'Yes' : 'No'}</div>
              </div>
            </div>
            <div>
              <h4 className={`font-medium mb-2 ${theme.textSecondary}`}>Window:</h4>
              <div className="space-y-1">
                <div><strong>Size:</strong> {window.innerWidth}x{window.innerHeight}</div>
                <div><strong>URL:</strong> {window.location.href}</div>
                <div><strong>Protocol:</strong> {window.location.protocol}</div>
                <div><strong>Host:</strong> {window.location.host}</div>
              </div>
            </div>
            <div>
              <h4 className={`font-medium mb-2 ${theme.textSecondary}`}>Performance:</h4>
              <div className="space-y-1">
                <div><strong>Load Time:</strong> {Math.round(performance.now())}ms</div>
                <div><strong>Memory:</strong> {(performance as unknown as { memory?: { usedJSHeapSize: number } }).memory ?
                  `${Math.round((performance as unknown as { memory: { usedJSHeapSize: number } }).memory.usedJSHeapSize / 1024 / 1024)}MB` : 'N/A'}</div>
                <div><strong>Connection:</strong> {(navigator as unknown as { connection?: { effectiveType: string } }).connection?.effectiveType || 'Unknown'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
